import PropTypes from 'prop-types'
import moment from 'moment'
import { Fragment, useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import ListContainer, { MenuItem } from './ListContainer'
import Notification from './Notification'
import ActiveAttacksList from './ActiveAttacksList'
import ScheduledAttackRow from './AttackRows/ScheduledAttackRow'
import ScheduledScenarioRow from './AttackRows/ScheduledScenarioRow'
import CompletedScenarioList from './CompletedScenarioList'
import NewCompletedScenariosNotifications from './NewCompletedScenariosNotifications'

import useAgent from 'hooks/use-agent'
import useAttackResource from 'hooks/use-attack-resource'
import useScheduleResource from 'hooks/use-schedule-resource'
import Text from 'components/common/Text'
import { willTriggerRunWithin } from 'components/schedules/trigger-utils'
import useScenarioResource from 'hooks/use-scenario-resource'

export function ListOfActiveAttacks({
  showHaltButton,
  clickableRows,
  onRowSelected,
  selectableRows,
  selectedRows
}) {
  const {
    data: [alfiApplicationQueryMetadata, alfiTrafficQueryMetadata]
  } = useAgent(
    ['/alfi/metadata/applicationQuery', '/alfi/metadata/trafficQuery'],
    [[], []],
    {
      shouldCache: true
    }
  )

  const {
    active: activeAttacks,
    doFetch: doFetchAttacks,
    activeScenarios,
    doFetchActiveScenarios,
    activeApplicationAttacks,
    doFetchApplicationAttacks
  } = useAttackResource({
    completed: false
  })

  return (
    <ActiveAttacksList
      alfiAttacks={activeApplicationAttacks}
      alfiMetadata={{
        applicationQuery: alfiApplicationQueryMetadata,
        trafficQuery: alfiTrafficQueryMetadata
      }}
      clickableRows={clickableRows}
      doFetchAlfiAttacks={doFetchApplicationAttacks}
      doFetchIlfiAttacks={doFetchAttacks}
      doFetchScenarios={doFetchActiveScenarios}
      ilfiAttacks={activeAttacks}
      scenarios={activeScenarios}
      selectableRows={selectableRows}
      selectedRows={selectedRows}
      showHaltButton={showHaltButton}
      onRowSelected={onRowSelected}
    />
  )
}

ListOfActiveAttacks.propTypes = {
  selectedRows: PropTypes.object,
  showHaltButton: PropTypes.bool,
  clickableRows: PropTypes.bool,
  selectableRows: PropTypes.bool,
  onRowSelected: PropTypes.func
}

ListOfActiveAttacks.defaultProps = {
  showHaltButton: true,
  clickableRows: true,
  selectableRows: false,
  onRowSelected: () => {}
}

export function ListOfCompletedScenarios({
  doFetchCompletedRuns,
  completedScenariosAwaitingResults,
  closePopover
}) {
  return (
    <CompletedScenarioList
      clickableRows={true}
      closePopover={closePopover}
      completedScenariosAwaitingResults={completedScenariosAwaitingResults}
      doFetchCompletedRuns={doFetchCompletedRuns}
      selectableRows={false}
      showHaltButton={false}
    />
  )
}

ListOfCompletedScenarios.propTypes = {
  completedScenariosAwaitingResults: PropTypes.array,
  doFetchCompletedRuns: PropTypes.func,
  closePopover: PropTypes.func
}

const isScenario = schedule => 'scenario_id' in schedule
const willRunInNext12Hours = schedule =>
  willTriggerRunWithin(schedule.trigger, moment(), moment().add(12, 'hours'))

export default function ActiveAttacks() {
  const { activeAttacksCount } = useAttackResource({
    completed: false
  })
  const location = useLocation()

  const {
    newCompletedScenarios,
    setNewCompletedScenarios,
    completedScenariosAwaitingResults,
    doFetchCompletedRuns
  } = useScenarioResource()

  const { schedules } = useScheduleResource({ interval: 20000 })
  const scheduledInNext12Hours = schedules.filter(willRunInNext12Hours)
  const hasSchedules = scheduledInNext12Hours.length > 0

  // show scenarios completed within the last 2 weeks, which have no results
  const currentlyOnScenariosRunsPage = location?.pathname.includes('/runs/')

  const scenariosToDisplay = useMemo(
    () => completedScenariosAwaitingResults.slice(0, 10),
    [completedScenariosAwaitingResults]
  )

  return (
    <Fragment>
      <Notification
        activeAttacksCount={activeAttacksCount}
        hasSchedules={hasSchedules}
        pendingResultsScenariosCount={scenariosToDisplay?.length}
      >
        {({ closePopover }) => {
          return (
            <ListContainer
              isEmpty={
                activeAttacksCount === 0 &&
                scenariosToDisplay.length === 0 &&
                !hasSchedules
              }
            >
              {activeAttacksCount > 0 && (
                <MenuItem headerName={`Active (${activeAttacksCount})`}>
                  <ListOfActiveAttacks />
                </MenuItem>
              )}
              {scenariosToDisplay.length > 0 && (
                <MenuItem
                  headerName={
                    <Text as="span">
                      Recently Completed
                      <Text
                        as="small"
                        css={{ display: 'inline' }}
                        weight="normal"
                      >
                        {' '}
                        in the last 24hrs
                      </Text>
                    </Text>
                  }
                >
                  <ListOfCompletedScenarios
                    closePopover={closePopover}
                    completedScenariosAwaitingResults={scenariosToDisplay}
                    doFetchCompletedRuns={doFetchCompletedRuns}
                  />
                </MenuItem>
              )}
              {hasSchedules && (
                <MenuItem
                  headerName={
                    <Text as="span">
                      Scheduled
                      <Text
                        as="small"
                        css={{ display: 'inline' }}
                        weight="normal"
                      >
                        {' '}
                        in the next 12hrs
                      </Text>
                    </Text>
                  }
                >
                  {scheduledInNext12Hours.map(schedule =>
                    isScenario(schedule) ? (
                      <ScheduledScenarioRow
                        key={schedule.guid}
                        schedule={schedule}
                      />
                    ) : (
                      <ScheduledAttackRow
                        key={schedule.guid}
                        schedule={schedule}
                      />
                    )
                  )}
                </MenuItem>
              )}
            </ListContainer>
          )
        }}
      </Notification>
      {currentlyOnScenariosRunsPage && (
        <NewCompletedScenariosNotifications
          newCompletedScenarios={newCompletedScenarios}
          setNewCompletedScenarios={setNewCompletedScenarios}
        />
      )}
    </Fragment>
  )
}
